import React, { useState, useEffect, useRef } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { ClipLoader } from "react-spinners";
import { Card, CardBody, CardHeader } from "reactstrap";
import useAutopilotChat from "../../hooks/useAutopilotChat";
import { useAuth } from "../../hooks/auth";
import { rotaInicial } from "../../routes/products";
import autopilotLogo from "../../assets/img/autopilot-logo.png";
import {
  AUTOPILOT_COLORS,
  autopilotCardStyle,
  autopilotEyebrowStyle,
  autopilotHeaderStyle,
  autopilotSecondaryButtonStyle,
} from "../../components/autopilot/styles";
import { QUICK_PROMPTS as SUGGESTIONS } from "../../components/autopilot/quickPrompts";

function renderInline(text) {
  const parts = [];
  const regex = /\*\*([^*]+)\*\*/g;
  let last = 0;
  let match;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > last) parts.push(text.slice(last, match.index));
    parts.push(<strong key={match.index}>{match[1]}</strong>);
    last = match.index + match[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}

function MessageBubble({ message }) {
  const isUser = message.role === "user";
  return (
    <div
      style={{
        display: "flex",
        justifyContent: isUser ? "flex-end" : "flex-start",
        marginBottom: "12px",
      }}
    >
      {!isUser && (
        <img
          src={autopilotLogo}
          alt="Autopilot"
          style={{
            width: "28px",
            height: "28px",
            borderRadius: "50%",
            marginRight: "8px",
            flexShrink: 0,
            marginTop: "2px",
            objectFit: "cover",
          }}
        />
      )}
      <div
        style={{
          maxWidth: "80%",
          padding: "10px 14px",
          borderRadius: isUser ? "16px 16px 4px 16px" : "16px 16px 16px 4px",
          background: isUser
            ? AUTOPILOT_COLORS.chatBubbleUserBg
            : AUTOPILOT_COLORS.chatBubbleAiBg,
          border: isUser ? "none" : `1px solid ${AUTOPILOT_COLORS.border}`,
          color: isUser ? AUTOPILOT_COLORS.chatBubbleUserText : AUTOPILOT_COLORS.chatBubbleAiText,
          fontSize: "0.85rem",
          lineHeight: "1.6",
        }}
      >
        {message.content.split("\n").filter((l) => l.trim()).map((line, i) => (
          <p key={i} style={{ margin: i === 0 ? 0 : "6px 0 0" }}>
            {renderInline(line)}
          </p>
        ))}
      </div>
    </div>
  );
}

// Mensagens de espera escalonadas (perceived performance). O TypingIndicator só
// monta enquanto `loading` é true, então os timers vivem exatamente o tempo da
// consulta e são limpos no unmount quando a resposta chega. Cada etapa tem
// variações e sorteamos uma por pergunta (no mount) pra não repetir sempre o
// mesmo texto e virar "efeito robô".
const LOADING_STAGES = [
  {
    after: 0,
    texts: [
      "Analisando sua pergunta...",
      "Deixa eu ver isso pra você...",
      "Certo, já estou verificando...",
      "Entendi, vou olhar os dados...",
    ],
  },
  {
    after: 5000,
    texts: [
      "Consultando os dados, só um instante...",
      "Puxando as informações, quase lá...",
      "Buscando no sistema, um momento...",
    ],
  },
  {
    after: 12000,
    texts: [
      "Está demorando um pouco mais que o normal, mas está saindo...",
      "Essa consulta é mais pesada, aguenta firme que já vem...",
      "Ainda processando, é bastante informação pra reunir...",
    ],
  },
  {
    after: 25000,
    texts: [
      "Ainda processando, obrigado pela paciência...",
      "Falta pouco, finalizando a análise...",
      "Quase lá, montando a resposta...",
    ],
  },
];

function pickStageTexts() {
  return LOADING_STAGES.map(
    (s) => s.texts[Math.floor(Math.random() * s.texts.length)]
  );
}

function TypingIndicator() {
  const [stage, setStage] = useState(0);
  // Sorteia uma variação por etapa uma única vez, no mount (uma nova pergunta =
  // novo TypingIndicator = novo sorteio).
  const [texts] = useState(pickStageTexts);

  useEffect(() => {
    const timers = LOADING_STAGES.slice(1).map((s, i) =>
      setTimeout(() => setStage(i + 1), s.after)
    );
    return () => timers.forEach(clearTimeout);
  }, []);

  return (
    <div style={{ display: "flex", alignItems: "center", marginBottom: "12px" }}>
      <img
        src={autopilotLogo}
        alt="Autopilot"
        style={{
          width: "28px",
          height: "28px",
          borderRadius: "50%",
          marginRight: "8px",
          flexShrink: 0,
          objectFit: "cover",
        }}
      />
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "10px",
          padding: "10px 16px",
          borderRadius: "16px 16px 16px 4px",
          background: "var(--everlog-card-surface-elevated)",
          border: "1px solid var(--everlog-card-border)",
        }}
      >
        <ClipLoader size={14} color={AUTOPILOT_COLORS.accent} />
        <span style={{ fontSize: "0.85rem", color: "var(--everlog-card-muted)" }}>
          {texts[stage]}
        </span>
      </div>
    </div>
  );
}

const CHAT_HEIGHT = "calc(100vh - 260px)";

const AutopilotChat = () => {
  const history = useHistory();
  const { user } = useAuth();
  const location = useLocation();
  // PO1-3508: quem chega de outra tela passa `origem.tela` no state; é essa tela
  // que vai no contexto do chat (senão, a própria URL do chat).
  const origemTela = location.state && location.state.origem && location.state.origem.tela;
  const { messages, loading, error, sendMessage, clearMessages } = useAutopilotChat(undefined, { tela: origemTela });
  const [input, setInput] = useState("");
  const messagesEndRef = useRef(null);
  const consumedLocationKeyRef = useRef(null);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, loading]);

  useEffect(() => {
    const initialMessage = location.state && location.state.initialMessage;
    if (!initialMessage || consumedLocationKeyRef.current === location.key) return;
    consumedLocationKeyRef.current = location.key;
    sendMessage(initialMessage);
    history.replace(location.pathname);
  }, [history, location.key, location.pathname, location.state, sendMessage]);

  const textareaRef = useRef(null);

  const handleSend = () => {
    const text = input.trim();
    if (!text || loading) return;
    setInput("");
    if (textareaRef.current) textareaRef.current.style.height = "auto";
    sendMessage(text);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleInputChange = (e) => {
    setInput(e.target.value);
    const el = e.target;
    el.style.height = "auto";
    el.style.height = Math.min(el.scrollHeight, 120) + "px";
  };

  const isEmpty = messages.length === 0;

  return (
    <div className="content">
      <Card style={{ ...autopilotCardStyle, background: "var(--everlog-card-hero-bg)", minHeight: CHAT_HEIGHT }}>
        <CardHeader style={autopilotHeaderStyle}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
              <img
                src={autopilotLogo}
                alt="Autopilot"
                style={{ width: 34, height: 34, borderRadius: 10, objectFit: "cover", flexShrink: 0 }}
              />
              <div>
                <div style={autopilotEyebrowStyle}>Assistente</div>
                <div style={{ color: AUTOPILOT_COLORS.title, fontSize: "1.05rem", fontWeight: 700, lineHeight: 1.2 }}>
                  Autopilot
                </div>
              </div>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
              {messages.length > 0 && (
                <button
                  onClick={clearMessages}
                  style={{
                    background: "var(--everlog-card-surface-elevated)",
                    border: "1px solid var(--everlog-card-border)",
                    borderRadius: "8px",
                    color: AUTOPILOT_COLORS.muted,
                    cursor: "pointer",
                    fontSize: "0.75rem",
                    padding: "5px 12px",
                    fontWeight: 500,
                  }}
                >
                  Limpar
                </button>
              )}
              <button
                onClick={() => history.push(rotaInicial(user))}
                className="btn btn-sm"
                style={autopilotSecondaryButtonStyle}
              >
                ← Voltar
              </button>
            </div>
          </div>
        </CardHeader>

        <CardBody style={{ display: "flex", flexDirection: "column", height: CHAT_HEIGHT, padding: 0 }}>
          {/* Messages area */}
          <div style={{ flex: 1, overflowY: "auto", padding: "20px 24px" }}>
            {isEmpty && (
              <div style={{ textAlign: "center", paddingTop: "32px" }}>
                <img
                  src={autopilotLogo}
                  alt="Autopilot"
                  style={{ width: "64px", height: "64px", borderRadius: "18px", objectFit: "cover", marginBottom: "12px" }}
                />
                <div style={{ color: AUTOPILOT_COLORS.muted, fontSize: "0.84rem", marginBottom: "24px" }}>
                  Pergunte sobre sua operação logística
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "0.6rem", maxWidth: "520px", margin: "0 auto" }}>
                  {SUGGESTIONS.map((item) => (
                    <button
                      key={item.label}
                      type="button"
                      onClick={() => sendMessage(item.prompt)}
                      className="everlog-card-prompt-button"
                      // receita do launcher (mesma dos tiles da home): borda
                      // accent + véu translúcido; hover na cor do ícone
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.65rem",
                        borderRadius: 12,
                        border: "1px solid var(--theme-accent-border)",
                        background: "var(--everlog-card-surface-soft)",
                        color: AUTOPILOT_COLORS.title,
                        textAlign: "left",
                        padding: "0.82rem 0.9rem",
                        cursor: "pointer",
                        "--prompt-accent": item.color,
                      }}
                    >
                      <span
                        style={{
                          width: 28,
                          height: 28,
                          borderRadius: 8,
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          background: `color-mix(in srgb, ${item.color} 14%, transparent)`,
                          color: item.color,
                          flexShrink: 0,
                          fontSize: "0.8rem",
                        }}
                      >
                        <i className={`tim-icons ${item.icon}`} />
                      </span>
                      <span style={{ fontSize: "0.82rem", fontWeight: 600 }}>{item.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((msg, i) => (
              <MessageBubble key={i} message={msg} />
            ))}

            {loading && <TypingIndicator />}

            {error && (
              <p style={{ color: AUTOPILOT_COLORS.statusDanger, fontSize: "0.82rem", textAlign: "center" }}>{error}</p>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input area */}
          <div
            style={{
              flexShrink: 0,
              padding: "12px 24px 16px",
              borderTop: "1px solid var(--everlog-card-border)",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "flex-end",
                gap: "10px",
                background: "var(--everlog-card-surface-elevated)",
                border: "1px solid var(--everlog-card-border)",
                borderRadius: "14px",
                padding: "10px 14px",
              }}
            >
              <textarea
                ref={textareaRef}
                value={input}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                placeholder="Digite sua mensagem..."
                disabled={loading}
                rows={1}
                style={{
                  flex: 1,
                  background: "none",
                  border: "none",
                  outline: "none",
                  color: "var(--everlog-card-text)",
                  fontSize: "0.875rem",
                  resize: "none",
                  lineHeight: "1.5",
                  overflowY: "auto",
                  fontFamily: "inherit",
                }}
              />
              <button
                onClick={handleSend}
                disabled={!input.trim() || loading}
                style={{
                  background: input.trim() && !loading
                    ? AUTOPILOT_COLORS.chatBubbleUserBg
                    : AUTOPILOT_COLORS.surface,
                  border: `1px solid ${AUTOPILOT_COLORS.border}`,
                  borderRadius: "8px",
                  width: "34px",
                  height: "34px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: input.trim() && !loading ? "pointer" : "not-allowed",
                  flexShrink: 0,
                  color: input.trim() && !loading ? AUTOPILOT_COLORS.chatBubbleUserText : AUTOPILOT_COLORS.muted,
                  fontSize: "14px",
                  transition: "background 0.15s",
                }}
              >
                {loading ? <ClipLoader size={14} color={AUTOPILOT_COLORS.accent} /> : "↑"}
              </button>
            </div>
            <p style={{ fontSize: "0.68rem", color: AUTOPILOT_COLORS.muted, textAlign: "center", margin: "6px 0 0", opacity: 0.5 }}>
              Enter para enviar · Shift+Enter para nova linha
            </p>
          </div>
        </CardBody>
      </Card>
    </div>
  );
};

export default AutopilotChat;
